import AppInnerCard from "Layout/AppInnerCard";
import AppInnerFooter from "Layout/AppInnerFooter";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import TransitionOnLoad from "Components/TransitionOnLoad";
import { Row, Col, Button } from "reactstrap";
import Details from "./Details";
import { ReactComponent as Arrow } from "../../../../../assets/utils/images/svgs/Arrow 3.svg";
import { ReactComponent as SortIcon } from "../../../../../assets/utils/images/svgs/sort-icon.svg";
import { ReactComponent as CommentIcon } from "../../../../../assets/utils/images/svgs/comments.svg";
import { ReactComponent as InfoIcon } from "../../../../../assets/utils/images/svgs/info.svg";
import { ReactComponent as PlusIcon } from "../../../../../assets/utils/images/svgs/plus.svg";
import FooterDropdown from "Components/IconDropdown/FooterDropdown";
import { sortOptions } from "../helpers";
import LegendModal from "./LegendModal";
import CommentsModal from "./CommentsModal";
import Bestuursinfo from "./Bestuursinfo";
import Table from "./Table";
import MessageModal from "./MessageModal";
import PlanscreenService from "Api/Verzoeken/Planscreen";
import { Loader } from "react-bootstrap-typeahead";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { getTip } from "../tooltips";
import RangeSlider from "../../../../../Components/RangeSlider";
import {
  getCachedFiltersForContract,
  getCommunityContractKey,
  setCachedFiltersForContract,
} from "helperFunctions/planscreenFiltersCache";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useSWR from "swr";
import PlanscreenSlider from "../Component/PlanscreenSlider";

const requestedContract = {
  id: -1,
  isOpen: true,
  name: "Ingepland",
  abbr: "Ingepland",
};

export default function Default() {
  const params = useParams();
  const navigate = useNavigate();

  const [details, setDetails] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [filtersCache, setFiltersCache] = useState({});
  const [usersToNotify, setUsersToNotify] = useState([]);
  const [emptyContracts, setEmptyContracts] = useState({});

  const [modals, setModals] = useState({
    legend: false,
    comments: false,
    message: false,
    schoolInfo: false,
  });


  const [error, setError] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [sortBy, setSortBy] = useState(1);
  const [contractViews, setContractViews] = useState({});

  const registerContractView = useCallback((contractId, view) => {
    setContractViews((prev) =>
      JSON.stringify(prev[contractId]) === JSON.stringify(view)
        ? prev
        : { ...prev, [contractId]: view }
    );
  }, []);

  const queryClient = useQueryClient();

  const extractFiltersArray = (res) => {
    if (!res) return null;
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data)) return res.data;
    return null;
  };

  const openContracts = useMemo(
    () => contracts.filter((ct) => {
      if (ct.name === "Ingepland") {
        return ct.isOpen && assignedUsers.length > 0;
      }
      return ct.isOpen;
    }),
    [contracts, assignedUsers]
  );

  const closedContracts = useMemo(
    () => contracts.filter((ct) => ct.isOpen !== true),
    [contracts]
  );


  const toggleContractVisibility = (id, value) => {
    setContracts((prev) =>
      prev.map((ct) => (ct.id === id ? { ...ct, isOpen: value } : ct))
    );
  };

  const sliderFrameRef = useRef(null);

  const handleGlobalSliderChange = useCallback((value) => {
    cancelAnimationFrame(sliderFrameRef.current);

    sliderFrameRef.current = requestAnimationFrame(() => {
      document
        .querySelectorAll(
          ".planscreen__availability-container .d-flex.gap-1"
        )
        .forEach((element) => {
          const container = element.closest(
            ".planscreen__availability-container"
          );

          if (!container) return;

          const maxScroll = Math.max(
            0,
            element.scrollWidth - container.clientWidth
          );

          const scrollLeft = (value / 100) * maxScroll;

          element.style.transform = `translate3d(-${scrollLeft}px, 0, 0)`;
        });
    });
  }, []);


  const detailsUrl = params.dayId
    ? `${params.dayId}?substitutionId=${params.id}&page=1&pageSize=150`
    : null;

  const {
    data: detailsResponse,
    error: detailsError,
    isLoading,
    mutate,
  } = useSWR(
    detailsUrl ? ["planscreen-details", detailsUrl] : null,
    () => PlanscreenService.getDetails(detailsUrl)
  );

  useEffect(() => {
    if (!detailsResponse) return;
    const res = detailsResponse;

    if (res?.status === false) {
      toast.error(
        "Shifts zijn niet beschikbaar voor deze gebruiker in de community"
      );
      return;
    }
    if (res?.data?.status === false || res?.data?.error === "invalid_link") {
      navigate("/no-page-found");
      return;
    }

    let payload = null;

    if (res?.data?.data) {
      payload = res.data.data;
    } else if (res?.data && !res?.data?.data) {
      payload = res.data;
    }

    if (payload) {
      setDetails([payload]);
      setContracts([
        requestedContract,
        ...(payload?.contracts || []),
      ]);
      setError(null);
    } else {
      setError(
        "Unexpected error occurred during fetching substitution details."
      );
    }
  }, [detailsResponse]);

  useEffect(() => {
    if (!details[0] || contracts.length === 0) return;

    const excludeWwz = details[0]?.excludeWwz ?? "0";
    const communityId = details[0]?.community?.id ?? "0";
    const contractIds = contracts
      .filter((ct) => ct.id > 0)
      .map((ct) => ct.id);

    const cachedFiltersByKey = {};
    const missingContractIds = contractIds.filter((contractId) => {
      const key = getCommunityContractKey(communityId, contractId); //124
      const queryKey = ["planscreen", "filters", communityId, contractId];
      if (filtersCache[key]) return false;
      const cachedQuery = queryClient.getQueryData(queryKey);
      if (Array.isArray(cachedQuery)) {
        cachedFiltersByKey[key] = cachedQuery;
        return false;
      }
      const cached = getCachedFiltersForContract(communityId, contractId);
      if (cached) {
        cachedFiltersByKey[key] = cached;
        return false;
      }
      return true;
    });

    if (Object.keys(cachedFiltersByKey).length > 0) {
      setFiltersCache((prev) => ({ ...prev, ...cachedFiltersByKey }));
    }

    if (missingContractIds.length === 0) return;

    let cancelled = false;

    const preloadFilters = async () => {
      await Promise.all(
        missingContractIds.map(async (contractId) => {
          try {
            const res = await PlanscreenService.getFilters(
              contractId,
              excludeWwz,
              communityId
            );
            const filtersData = extractFiltersArray(res);
            if (!filtersData || cancelled) return;

            const key = getCommunityContractKey(communityId, contractId);
            const queryKey = ["planscreen", "filters", communityId, contractId];

            setCachedFiltersForContract(communityId, contractId, filtersData);
            queryClient.setQueryData(queryKey, filtersData);
            setFiltersCache((prev) => {
              if (prev[key]) return prev;
              return { ...prev, [key]: filtersData };
            });
          } catch (e) {
            console.error(
              `[ERROR] Planscreen preload filters failed for contract ${contractId}:`,
              e
            );
          }
        })
      );
    };

    preloadFilters();

    return () => {
      cancelled = true;
    };
  }, [details, contracts]);

  const handleEmptyChange = useCallback((contractId, isEmpty) => {
    setEmptyContracts((prev) =>
      prev[contractId] === isEmpty ? prev : { ...prev, [contractId]: isEmpty }
    );
  }, []);

  return (
    <>
      <TransitionOnLoad
        component="div"
        className="TabsAnimation"
        appear={true}
        timeout={1500}
        enter={false}
        exit={false}
      >
        <>
          <LegendModal
            isOpen={modals.legend}
            setIsOpen={(value) =>
              setModals((prev) => ({ ...prev, legend: !prev.legend }))
            }
          />
          <CommentsModal
            isOpen={modals.comments}
            setIsOpen={(value) =>
              setModals((prev) => ({ ...prev, comments: !prev.comments }))
            }
          />
          <MessageModal
            isOpen={modals.message}
            setIsOpen={(value) =>
              setModals((prev) => ({ ...prev, message: !prev.message }))
            }
            usersToNotify={usersToNotify}
            setUsersToNotify={setUsersToNotify}
          />
          <Bestuursinfo
            dayId={params.dayId}
            schoolInfoModalOpen={modals.schoolInfo}
            setSchoolInfoModalOpen={(value) =>
              setModals((prev) => ({ ...prev, schoolInfo: value }))
            }
          />
          <AppInnerCard
            headingProps={{
              useDefaultPath: false,
              additionalBreadcrumbs: [
                {
                  href: "/#/verzoeken/overzicht/" + params.id,
                  label: "Verzoek " + params.id,
                },
              ],
            }}
            wrapperClassName="app-inner-layout__wrapper--coordinator-planboard p-0"
            mainCardClassName="main-card--coordinator-planboard p-0 mb-0 px-3"
            cardBodyClassname="planscreen__table-responsive table-responsive p-0 "
          >
            <Row>
              <Col className="p-0">
                {isLoading && (
                  <Loader
                    type="ball-grid-pulse"
                    className={"mt-3 d-flex mx-auto justify-content-center"}
                  />
                )}
                {!isLoading && !error && details && <Details data={details} />}
                {error && <div className="text-danger">{error}</div>}
              </Col>
            </Row>
          </AppInnerCard>

          {openContracts.map((ct, i) => {
            const isEmpty = emptyContracts[ct.id] === true;
            // last *visible* card gets the bottom margin
            const lastVisibleId = [...openContracts].reverse().find(c => !emptyContracts[c.id])?.id;
            const isLast = ct.id === lastVisibleId;
            // const isLast = i + 1 === openContracts.length;
            const filtersCacheKey = getCommunityContractKey(
              details[0]?.community?.id,
              ct.id
            );

            console.log("Rerendering Page");

            return (
              <div key={ct.id} className={isEmpty ? "d-none" : ""}>
                <AppInnerCard
                  showPageHeading={false}
                  wrapperClassName="app-inner-layout__wrapper--coordinator-planboard p-0"
                  mainCardClassName="main-card--coordinator-planboard p-0 px-3"
                  cardBodyClassname="planscreen__table-responsive table-responsive p-0"
                  cardClassname={isLast ? "mb-5" : ""}
                  key={ct.id}
                >
                  <Row>
                    <Col className="p-0">
                      <Table
                        details={details}
                        sortBy={sortBy}
                        contract={ct}
                        shiftId={params.dayId}
                        setContracts={setContracts}
                        toggleContractVisibility={toggleContractVisibility}
                        setUsersToNotify={setUsersToNotify}
                        setIsOpenMessageModal={() =>
                          setModals((prev) => ({
                            ...prev,
                            message: !prev.message,
                          }))
                        }
                        usersToNotify={usersToNotify}
                        enabledSorting={sortBy}
                        excludeWwz={details[0]?.excludeWwz}
                        communityId={details[0]?.community?.id}
                        assignedUsers={assignedUsers}
                        setAssignedUsers={setAssignedUsers}
                        allContracts={contracts}
                        contractFilters={filtersCache}
                        preloadedFilters={filtersCache[filtersCacheKey]}
                        onEmptyChange={handleEmptyChange}
                        contractViews={contractViews}
                        registerContractView={registerContractView}
                      />
                    </Col>
                  </Row>
                </AppInnerCard>
              </div>
            );
          })}
        </>
      </TransitionOnLoad>

      <AppInnerFooter
        footerWrapperClassName="planscreen__footer"
        cardFooterClassName="flex-wrap flex-xxl-nowrap gap-2 justify-content-center justify-content-xxl-between chat-layout-footer__inner"
      >
        <div className="flex-xxl-grow-1 flex-wrap flex-xxl-nowrap d-flex justify-content-center justify-content-xxl-between gap-2 footer--coordinator-planboard__checkbox-container">
          <div className="d-flex align-items-stretch gap-3">
            <FooterDropdown
              options={sortOptions}
              value={sortBy}
              setValue={(value) => setSortBy(value)}
              icon={
                <Tippy
                  content="Vervangers sorteren"
                  placement="top"
                  arrow
                >
                  <div>
                    <SortIcon />
                  </div>
                </Tippy>
              }
            />

            <FooterDropdown
              value={modals.legend}
              setValue={(value) =>
                setModals((prev) => ({ ...prev, legend: !prev.legend }))
              }
              icon={
                <Tippy
                  content="Legenda"
                  placement="top"
                  arrow
                >
                  <div>
                    <InfoIcon />
                  </div>
                </Tippy>
              }
            />

            <FooterDropdown
              value={modals.schoolInfo}
              setValue={(value) => {
                setModals((prev) => ({ ...prev, schoolInfo: value }));
              }}
              icon={
                <Tippy
                  content="Bestuurinfo"
                  placement="top"
                  arrow
                >
                  <span>
                    <CommentIcon fill="red" />
                  </span>
                </Tippy>
              }
            />

            {closedContracts.length > 0 && (
              <>
                <div className="planscreen__footer-line"></div>

                {closedContracts.map((ct) => (
                  <FooterDropdown
                    key={ct.id}
                    value={ct.isOpen}
                    setValue={() => toggleContractVisibility(ct.id, true)}
                    icon={
                      <Tippy
                        content={getTip("CONTRACT_SHOW")}
                        placement="top"
                        arrow
                      >
                        <span className="d-flex gap-1 align-middle">
                          <div className="mt-2">{ct.abbr}</div>
                          <PlusIcon />
                        </span>
                      </Tippy>
                    }
                    buttonClassname="btn-color-style"
                  />
                ))}

              </>
            )}


          </div>

          <div className="d-flex justify-content-center align-items-center">
            <div className="planscreen__footer-line-end"></div>
            <div className="ms-2 me-5">

              <PlanscreenSlider onChange={handleGlobalSliderChange} />
            </div>
            <Tippy
              content="Terug naar verzoek"
              placement="top"
              arrow
            >
              <Button
                size="sm"
                outline
                className="mx-2 btn-color-style footer-btn-styling"
                color="primary"
                onClick={() => navigate("/verzoeken/overzicht/" + params.id)}
              >
                <Arrow />
              </Button>
            </Tippy>
          </div>
        </div>
      </AppInnerFooter>
    </>
  );
}